import { useMutation, useQuery } from "@apollo/client/react";
import { Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { graphql } from "gql.tada";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../components/form/CreationForm";
import { PageHeader } from "../../components/PageHeader";
import { FormSkeleton } from "../../components/SkeletonLoader";
import { useConfirmModal } from "../../components/ConfirmModal";

const EVENT_QUERY = graphql(`
  query event($id: ID!) {
    event(id: $id) {
      id
      title
      description
      startAt
      endAt
      visibleAt
      eventVenueId
    }
  }
`);

const VENUES_QUERY = graphql(`
  query eventVenues($pageInfo: PageInfo!) {
    eventVenues(page: $pageInfo) {
      nodes {
        id
        name
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const CREATE_EVENT_MUTATION = graphql(`
  mutation eventCreate(
    $title: String!
    $description: String!
    $startAt: DateTime!
    $endAt: DateTime!
    $eventVenueId: ID
  ) {
    eventCreate(
      input: {
        title: $title
        description: $description
        startAt: $startAt
        endAt: $endAt
        eventVenueId: $eventVenueId
      }
    ) {
      id
    }
  }
`);

const UPDATE_EVENT_MUTATION = graphql(`
  mutation eventUpdate(
    $id: ID!
    $title: String!
    $description: String!
    $startAt: DateTime!
    $endAt: DateTime!
    $visibleAt: DateTime
    $eventVenueId: ID
  ) {
    eventUpdate(
      id: $id
      input: {
        title: $title
        description: $description
        startAt: $startAt
        endAt: $endAt
        visibleAt: $visibleAt
        eventVenueId: $eventVenueId
      }
    ) {
      id
    }
  }
`);

export const EventForm = () => {
  const navigate = useNavigate();
  const eventId = useParams().eventId;
  const { confirm, modal } = useConfirmModal();
  const { loading, data } = useQuery<any>(EVENT_QUERY, {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variables: { id: eventId! },
    skip: !eventId,
  });
  const [createEvent] = useMutation<any>(CREATE_EVENT_MUTATION);
  const [updateEvent] = useMutation<any>(UPDATE_EVENT_MUTATION);
  const { loading: venuesLoading, data: venuesData } = useQuery<any>(
    VENUES_QUERY,
    {
      variables: { pageInfo: { after: null, first: 100 } },
    },
  );

  if (loading || venuesLoading) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title={eventId ? "Update Event" : "Create Event"}
          showBackButton
          showBreadcrumbs={false}
        />
        <FormSkeleton fields={5} />
      </div>
    );
  }

  return (
    <div className="wp-fade-in">
      {modal}
      <PageHeader
        title={eventId ? "Update Event" : "Create Event"}
        showBackButton
        showBreadcrumbs={false}
      />
      <CreationForm
        schemaDefinition={{
          title: {
            type: "INPUT",
            label: "Title",
            yupConfig: yup.string().required(),
            inputType: "text",
            defaultValue: data?.event?.title,
            placeholder: "Enter the event title",
          },
          description: {
            type: "RICH_TEXT_EDITOR",
            label: "Description contents",
            yupConfig: yup.string().required(),
            defaultValue: data?.event?.description,
          },
          startAt: {
            type: "INPUT",
            label: "Start at",
            yupConfig: yup.date().required(),
            inputType: "date",
            placeholder: "Enter the event start date",
            defaultValue: data?.event?.startAt,
          },
          endAt: {
            type: "INPUT",
            label: "End at",
            yupConfig: yup.date().required(),
            inputType: "date",
            placeholder: "Enter the event end date",
            defaultValue: data?.event?.endAt,
          },
          ...(eventId && {
            visibleAt: {
              type: "INPUT" as const,
              label: "Visible at",
              yupConfig: yup.date().nullable(),
              inputType: "date" as const,
              placeholder:
                "Enter the date when the event will be visible to the public",
              defaultValue: data?.event?.visibleAt ?? null,
            },
          }),
          ...(venuesData?.eventVenues.nodes.length && {
            eventVenueId: {
              type: "SELECT" as const,
              label: "Venue",
              yupConfig: yup.string().nullable(),
              options:
                venuesData?.eventVenues.nodes.map((venue: any) => ({
                  label: venue.name,
                  value: venue.id,
                })) ?? [],
              defaultValue: data?.event?.eventVenueId ?? null,
            },
          }),
        }}
        onSubmit={async (values) => {
          if (eventId) {
            if (values.eventVenueId !== data?.event?.eventVenueId) {
              const confirmed = await confirm({
                title: "Change Venue",
                message: (
                  <Text>
                    The venue of the event has been changed, this will unassign
                    all existing seats from their reservations.
                    <br />
                    <br />
                    Reservation tickets will still be accessible, but they will
                    not be assigned to a seat anymore. New seats will be
                    associated after the venue change.
                    <br />
                    <br />
                    Are you sure you want to change the venue?
                  </Text>
                ),
                confirmLabel: "Change Venue",
                type: "warning",
              });

              if (!confirmed) {
                return;
              }
            }

            await updateEvent({
              variables: {
                id: eventId,
                title: values.title,
                description: values.description,
                startAt: values.startAt,
                endAt: values.endAt,
                visibleAt:
                  !values.visibleAt || values.visibleAt === ""
                    ? null
                    : values.visibleAt,
                eventVenueId:
                  !values.eventVenueId || values.eventVenueId === ""
                    ? null
                    : values.eventVenueId,
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
            });
            notifications.show({
              title: "Event updated",
              message: "The event has been updated",
              color: "green",
              autoClose: 3000,
            });
          } else {
            const newEventId = await createEvent({
              variables: {
                title: values.title,
                description: values.description,
                startAt: values.startAt,
                endAt: values.endAt,
                eventVenueId:
                  !values.eventVenueId || values.eventVenueId === ""
                    ? null
                    : values.eventVenueId,
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
              return data.data?.eventCreate.id;
            });
            navigate("/events/" + newEventId);
          }
        }}
      />
    </div>
  );
};
